import Dexie from 'dexie';
import { IAutoSaveDoc, IAutoSaveDocChanges } from './autosave.interface';
import { isDocHashCustomDoc, isK3000domain } from 'src/app/core/common';
import { FileHashManager } from './file-hash-manager';
import { ExamDocumentType } from '../exam/enums/document-type.enum';


// migrations
// DBVersion = 1
// tables: pdfDocuments, htmlDocuments
// DBVersion = 2
// tables: allDocuments
// DBVersion = 3
// tables: table


const DBName = 'AsgardAutoSaveStorage';
const DBVersion = 3;
const DBTableDocs = 'table';
const DocumentStoreTime = 3; // days
const INDEXES = ['documentHash'];

export function dbName(userId: string): string {
    return `${DBName}-${isK3000domain() ? 'K3000' : 'Alinea'}-${userId}`;
}

export class AutoSaveStore extends Dexie {

    /* primary key is string: documentHash */
    private allDocuments: Dexie.Table<IAutoSaveDoc, string>;

    private get expiresDate(): number {
        const now = new Date().getTime();
        return now + (DocumentStoreTime * 1000 * 60 * 60 * 24);
    }

    public async getDoc(documentHash: string | null): Promise<IAutoSaveDoc | null> {
        const doc = (await this.allDocuments.toArray()).find(e => e.documentHash === documentHash);
        if (!doc) {
            return null;
        }
        return doc;
    }

    private async addDoc(doc: IAutoSaveDoc): Promise<void> {
        await this.allDocuments.put(doc);
    }

    private async updateDoc(dochash: string, changes: { [key: string]: any }): Promise<void> {
        await this.allDocuments.update(dochash, changes);
    }

    constructor(userId: string) {
        super(dbName(userId));
        // clealing prev versions of db
        this.version(1).stores({
            pdfDocuments: null,
            htmlDocuments: null
        });
        this.version(2).stores({
            allDocuments: null,
        });
        // end
        this.version(DBVersion).stores({
            table: INDEXES.join(','),
        });
        this.allDocuments = this.table(DBTableDocs);
        this.open().then(() => {
            this.cleanUpExpiredDocs();
            // Database opened successfully
        }).catch(() => {
            // Error occurred
            console.error('autosave storage error');
        });
    }

    public async updateDocumentPage(documentHash: string, pageIdx: number): Promise<void> {
        const doc = await this.getDoc(documentHash);
        if (doc) {
            this.updateDoc(documentHash, { pageIdx });
        }
    }

    public async documentPage(documentHash: string): Promise<number | null> {
        const doc = await this.getDoc(documentHash);

        if (doc) {
            return doc.pageIdx;
        }

        return null;
    }

    public async updateReadingPosition(documentHash: string, readingItemIdx: number | null,
        readingItemPageIdx: number | null = null): Promise<void> {
        const doc = await this.getDoc(documentHash);

        if (doc) {
            this.updateDoc(documentHash, { readingItemIdx, readingItemPageIdx });
        }
    }

    public async getUnsavedDocuments(): Promise<IAutoSaveDoc[]> {
        return (await this.allDocuments.toArray()).filter(e => e.unsavedChanges && e.examFileType === null);
    }

    public async getUnsavedExamDocuments(): Promise<IAutoSaveDoc[]> {
        return (await this.allDocuments.toArray()).filter(e => e.unsavedChanges && e.examFileType !== null);
    }

    public async updateDocumentChanges(data: IAutoSaveDocChanges, examDocType: ExamDocumentType | null = null): Promise<void> {
        const doc = await this.getDoc(data.documentHash);
        // console.log(doc, 'doc', 'data: ', data);
        if (doc) {
            if (data.fileName) {
                await this.updateDoc(doc.documentHash, {
                    unsavedChanges: data.unsavedChanges,
                    fileName: data.fileName,
                    pageIdx: data.pageIdx,
                    readingItemIdx: data.readingItemIdx,
                    isSTIFile: data.isSTIFile,
                });
            } else {
                await this.updateDoc(doc.documentHash, {
                    unsavedChanges: data.unsavedChanges,
                    pageIdx: data.pageIdx,
                    readingItemIdx: data.readingItemIdx,
                    isSTIFile: data.isSTIFile,
                });
            }
        } else {
            await this.addOriginalFile(data.documentHash, null, data.fileName, data.unsavedChanges, examDocType, data.isSTIFile);
        }
    }

    public async addOriginalFile(documentHash: string | null, originalFile: File | null, fileName: string | null,
        unsavedChanges: string | null = null, examFileType: ExamDocumentType | null = null, isSTIFile: boolean = false,): Promise<void> {
        if (documentHash) {
            const doc = await this.getDoc(documentHash);
            if (!doc) {
                const dataToSave: IAutoSaveDoc = {
                    expiresDate: this.expiresDate,
                    unsavedChanges,
                    originalFile,
                    documentHash,
                    fileName,
                    pageIdx: null,
                    readingItemIdx: null,
                    readingItemPageIdx: null,
                    examFileType,
                    isSTIFile,
                };
                await this.addDoc(dataToSave);
            } else {
                await this.updateDoc(documentHash, {
                    expiresDate: this.expiresDate,
                    fileName,
                    unsavedChanges: (unsavedChanges ?? doc.unsavedChanges),
                    originalFile,
                    isSTIFile: isSTIFile || !!doc.isSTIFile,
                });
            }
        }
    }

    public async isUnsavedChanges(dochash: string | null): Promise<string | null> {
        if (dochash === null) {
            return null;
        }
        const doc = await this.getDoc(dochash);
        if (doc) {
            return doc.unsavedChanges;
        }
        return null;
    }

    private async cleanUpExpiredDocs(): Promise<void> {
        const now = new Date().getTime();
        const collection = await this.allDocuments.toArray();
        for (const e of collection) {
            if (e.expiresDate && e.expiresDate < now || !e.unsavedChanges) {
                if (isDocHashCustomDoc(e.documentHash)) {
                    await this.allDocuments.delete(e.documentHash);
                } else {
                    await this.removeUnsavedChangesAndFile(e.documentHash, true);
                }
            }
        }
    }

    public async removeUnsavedChangesAndFile(documentHash: string | null, removeFile: boolean = false): Promise<void> {
        const doc = await this.getDoc(documentHash);
        if (doc && documentHash) {
            const changes: any = {
                unsavedChanges: null
            };

            if (removeFile) {
                changes.originalFile = null;
            }
            await this.updateDoc(documentHash, changes);
        }
    }


    public async addSavedFileAndUpdateReadingPos(file: File, readingItemIdx: number | null,
        readingItemPageIdx: number | null = null): Promise<void> {
        if (file) {
            const newHash = await FileHashManager.calculateFileHash(file);
            await this.addOriginalFile(newHash, file, file.name);
            await this.updateReadingPosition(newHash, readingItemIdx, readingItemPageIdx);
        }
    }

    public async deleteAllSavedFiles(keepExamFiles: boolean = false) {
        const allDocs = await this.allDocuments.filter(x => keepExamFiles ? x.examFileType === null : true).toArray()
        for (const e of allDocs) {
            await this.removeUnsavedChangesAndFile(e.documentHash, true);
        }
    }

    public async deleteAllExamFiles() {
        const examDocs = await this.allDocuments.filter(x => x.examFileType !== null).toArray()
        for (const e of examDocs) {
            await this.deleteDocument(e);
        }
    }

    public async updateOriginalFile(documentHash: string, file: File): Promise<void> {
        await this.updateDoc(documentHash, { originalFile: file});
    }

    private deleteDocument(doc: IAutoSaveDoc): Promise<void> {
        return this.allDocuments.delete(doc.documentHash);
    }
}


